import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { History, User, Clock, ArrowRight, ChevronDown, ChevronUp, FileText } from 'lucide-react';
import { useAuditHistory, parseAuditDifferences, AuditLogEntry } from '@/api/hooks/useAudit';
import { useAssetMetadataHistory } from '@/api/hooks/useMetadata';
import type { MetadataHistoryEntry } from '@/types/metadata.types';
import { cn, formatDateTime as formatDateTimeI18n } from '@/lib/utils';

interface AuditHistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entityName: string;
  entityId: string;
  title?: string;
}

// Action badge colors
const actionColors: Record<string, string> = {
  Create: 'bg-green-100 text-green-700 hover:bg-green-100',
  Update: 'bg-blue-100 text-blue-700 hover:bg-blue-100',
  Delete: 'bg-red-100 text-red-700 hover:bg-red-100',
  Modified: 'bg-blue-100 text-blue-700 hover:bg-blue-100',
  Added: 'bg-green-100 text-green-700 hover:bg-green-100',
};

// Locale comes from the active i18n language via the shared helper — pinning
// 'es-MX' here left audit timestamps in Spanish under the English UI.
function formatDateTime(dateString: string | undefined): string {
  if (!dateString) return '-';
  try {
    return formatDateTimeI18n(dateString);
  } catch {
    return dateString;
  }
}

function AuditEntryCard({
  entry,
  t,
  isExpanded,
  onToggle,
}: {
  entry: AuditLogEntry;
  t: (key: string) => string;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const differences = parseAuditDifferences(entry.differences);
  const actionKey = entry.action.toLowerCase();
  const actionLabel = t(`auditHistory.actions.${actionKey}`) || entry.action;
  const hasDetails = differences.length > 0 || entry.action === 'Create';

  return (
    <div className="border rounded-lg mb-2 overflow-hidden">
      <Button
        variant="ghost"
        className="w-full justify-between p-4 h-auto hover:bg-muted/50"
        onClick={onToggle}
        disabled={!hasDetails}
      >
        <div className="flex items-center gap-3">
          <Badge
            variant="secondary"
            className={cn(
              'font-medium',
              actionColors[entry.action] || 'bg-gray-100 text-gray-700'
            )}
          >
            {actionLabel}
          </Badge>
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <Clock className="h-3.5 w-3.5" />
            {formatDateTime(entry.timestamp)}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <User className="h-3.5 w-3.5" />
            {entry.userName || t('auditHistory.systemUser')}
          </div>
          {hasDetails && (
            isExpanded ? (
              <ChevronUp className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            )
          )}
        </div>
      </Button>

      {isExpanded && hasDetails && (
        <div className="px-4 pb-4 border-t bg-muted/30">
          {differences.length > 0 ? (
            <div className="space-y-2 pt-3">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                {t('auditHistory.changesLabel')}
              </p>
              <div className="space-y-2">
                {differences.map((diff, idx) => (
                  <div
                    key={idx}
                    className="flex items-start gap-2 text-sm bg-background rounded-md p-2"
                  >
                    <span className="font-medium min-w-[120px] text-muted-foreground">
                      {diff.field}:
                    </span>
                    <span className="text-red-600 line-through">
                      {diff.oldValue || t('auditHistory.emptyValue')}
                    </span>
                    <ArrowRight className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                    <span className="text-green-600">
                      {diff.newValue || t('auditHistory.emptyValue')}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground pt-3">
              {entry.action === 'Create'
                ? t('auditHistory.recordCreated')
                : t('auditHistory.noChangesRecorded')}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-3">
      {[1, 2, 3].map((i) => (
        <div key={i} className="border rounded-lg p-4 space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Skeleton className="h-5 w-16" />
              <Skeleton className="h-4 w-32" />
            </div>
            <Skeleton className="h-4 w-24" />
          </div>
        </div>
      ))}
    </div>
  );
}

function MetadataHistoryCard({
  entry,
  t,
}: {
  entry: MetadataHistoryEntry;
  t: (key: string) => string;
}) {
  return (
    <div className="border rounded-lg p-4 mb-2 bg-background">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <Badge variant="outline" className="font-medium">
              {entry.fieldLabel || entry.fieldKey}
            </Badge>
            <span className="text-xs text-muted-foreground">
              {entry.fieldKey}
            </span>
          </div>
          <div className="flex items-center gap-2 text-sm bg-muted/30 rounded-md p-2">
            <span className="text-red-600 line-through">
              {entry.oldValue || t('auditHistory.emptyValue')}
            </span>
            <ArrowRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <span className="text-green-600">
              {entry.newValue || t('auditHistory.emptyValue')}
            </span>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
        <span className="flex items-center gap-1">
          <Clock className="h-3 w-3" />
          {formatDateTime(entry.changedDate)}
        </span>
        <span className="flex items-center gap-1">
          <User className="h-3 w-3" />
          {entry.changedByUserName || t('auditHistory.systemUser')}
        </span>
      </div>
    </div>
  );
}

export function AuditHistoryDialog({
  open,
  onOpenChange,
  entityName,
  entityId,
  title,
}: AuditHistoryDialogProps) {
  const { t } = useTranslation(['assets', 'common']);
  const { data: auditLogs, isLoading, error } = useAuditHistory(entityName, entityId, open);
  const {
    data: metadataHistory,
    isLoading: metadataLoading,
    error: metadataError,
  } = useAssetMetadataHistory(entityId, open && entityName === 'Asset');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<string>('general');

  const handleToggle = (id: number) => {
    setExpandedId(expandedId === id ? null : id);
  };

  // Show tabs only for Asset entities (which have metadata)
  const showMetadataTab = entityName === 'Asset';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            {t('auditHistory.title')}
          </DialogTitle>
          <DialogDescription>
            {title
              ? t('auditHistory.descriptionWithName', { name: title })
              : t('auditHistory.description')}
          </DialogDescription>
        </DialogHeader>

        {showMetadataTab ? (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="general" className="flex items-center gap-2">
                <History className="h-4 w-4" />
                {t('auditHistory.generalTab', 'General')}
              </TabsTrigger>
              <TabsTrigger value="metadata" className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                {t('auditHistory.metadataTab', 'Custom Fields')}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="general" className="mt-4">
              <ScrollArea className="h-[350px] pr-4">
                {isLoading ? (
                  <LoadingSkeleton />
                ) : error ? (
                  <div className="text-center py-8">
                    <History className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">{t('auditHistory.errorLoading')}</p>
                  </div>
                ) : auditLogs && auditLogs.length > 0 ? (
                  <div className="space-y-0">
                    {auditLogs.map((entry) => (
                      <AuditEntryCard
                        key={entry.id}
                        entry={entry}
                        t={t}
                        isExpanded={expandedId === entry.id}
                        onToggle={() => handleToggle(entry.id)}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <History className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">{t('auditHistory.noHistory')}</p>
                  </div>
                )}
              </ScrollArea>
            </TabsContent>

            <TabsContent value="metadata" className="mt-4">
              <ScrollArea className="h-[350px] pr-4">
                {metadataLoading ? (
                  <LoadingSkeleton />
                ) : metadataError ? (
                  <div className="text-center py-8">
                    <FileText className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">{t('auditHistory.errorLoading')}</p>
                  </div>
                ) : metadataHistory && metadataHistory.length > 0 ? (
                  <div className="space-y-0">
                    {metadataHistory.map((entry, index) => (
                      <MetadataHistoryCard
                        key={`${entry.assetMetadataValueId}-${index}`}
                        entry={entry}
                        t={t}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <FileText className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">
                      {t('auditHistory.noMetadataHistory', 'No custom field changes recorded')}
                    </p>
                  </div>
                )}
              </ScrollArea>
            </TabsContent>
          </Tabs>
        ) : (
          <ScrollArea className="h-[400px] pr-4">
            {isLoading ? (
              <LoadingSkeleton />
            ) : error ? (
              <div className="text-center py-8">
                <History className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">{t('auditHistory.errorLoading')}</p>
              </div>
            ) : auditLogs && auditLogs.length > 0 ? (
              <div className="space-y-0">
                {auditLogs.map((entry) => (
                  <AuditEntryCard
                    key={entry.id}
                    entry={entry}
                    t={t}
                    isExpanded={expandedId === entry.id}
                    onToggle={() => handleToggle(entry.id)}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <History className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">{t('auditHistory.noHistory')}</p>
              </div>
            )}
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
}
